import axios, { type AxiosInstance } from 'axios';
import type { ReceiptFieldFlags } from '@mlm/shared';
import { useAuthStore, type AuthUser } from './auth-store';
import { DEFAULT_COIN_LABEL } from './coin-label';
import { queryClient } from './query-client';

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';

export const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/**
 * Turn a thrown request error into a human message. Axios swallows the server
 * body behind a generic "Request failed with status code 4xx"; this digs out
 * the API's `{ error, message, issues }` so toasts/dialogs show the real reason.
 */
export function apiErrorMessage(e: unknown, fallback = 'Request failed'): string {
  if (axios.isAxiosError(e)) {
    const data = e.response?.data as
      | {
          error?: string;
          message?: string;
          issues?: Array<{ path?: (string | number)[]; message?: string }>;
        }
      | undefined;
    if (data?.issues?.length) {
      return data.issues
        .map((i) => `${(i.path ?? []).join('.') || 'value'}: ${i.message ?? 'invalid'}`)
        .join('; ');
    }
    if (data?.message) return data.message;
    if (data?.error) return data.error;
    if (e.message) return e.message;
  }
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}

let refreshInFlight: Promise<string | null> | null = null;
async function refreshTokenOnce(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const res = await axios.post<{ accessToken: string }>(
          `${BASE_URL}/auth/refresh`,
          {},
          { withCredentials: true },
        );
        return res.data.accessToken;
      } catch {
        return null;
      } finally {
        setTimeout(() => {
          refreshInFlight = null;
        }, 0);
      }
    })();
  }
  return refreshInFlight;
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    if (
      error.response?.status === 401 &&
      original &&
      !original._retried &&
      !String(original.url ?? '').includes('/auth/refresh') &&
      !String(original.url ?? '').includes('/auth/login') &&
      // A wrong-current-password 401 from the change-password form is a real
      // rejection, not an expired token — don't refresh+replay it (that burns
      // the account's 10/hr attempts, two per typo).
      !String(original.url ?? '').includes('/me/password')
    ) {
      original._retried = true;
      const newToken = await refreshTokenOnce();
      if (newToken) {
        const user = useAuthStore.getState().user;
        if (user) useAuthStore.getState().setSession(newToken, user);
        original.headers.Authorization = `Bearer ${newToken}`;
        return api.request(original);
      }
      useAuthStore.getState().clear();
      queryClient.clear();
    }
    return Promise.reject(error);
  },
);

type LoginResponse = {
  accessToken: string;
  user: Omit<AuthUser, 'branchId' | 'mustChangePassword'> & {
    scope?: { branchId?: string | null };
    mustChangePassword?: boolean;
  };
};

export async function login(args: {
  email: string;
  password: string;
  totp?: string;
}): Promise<{ accessToken: string; user: AuthUser }> {
  const res = await api.post<LoginResponse>('/auth/login', args);
  const u = res.data.user;
  return {
    accessToken: res.data.accessToken,
    user: {
      id: u.id,
      email: u.email,
      fullName: u.fullName,
      roles: u.roles,
      perms: u.perms,
      totpEnabled: u.totpEnabled,
      mustChangePassword: u.mustChangePassword ?? false,
      branchId: u.scope?.branchId ?? null,
    },
  };
}

export async function logout(): Promise<void> {
  try {
    await api.post('/auth/logout');
  } catch {
    /* ignore */
  }
  useAuthStore.getState().clear();
  // Drop every cached query so the next signed-in user can't see the
  // previous account's data flash before refetches complete.
  queryClient.clear();
}

/** Change the signed-in cashier's own password (requires the current one). */
export async function changePassword(args: {
  currentPassword: string;
  newPassword: string;
}): Promise<void> {
  await api.post('/me/password', args);
}

/**
 * Restore an in-memory session from the refresh cookie on the device. Called
 * once at app boot — the auth store is memory-only, so a page refresh starts
 * with `token=null`; without this the cashier would always be bounced to the
 * login screen even when the refresh cookie is still valid.
 *
 * `/auth/refresh` only returns the token, so we follow up with `/me` to
 * re-hydrate roles/perms/branch. Returns `true` if restored, `false` if the
 * cookie was missing/expired and the user must log in again.
 */
export async function restoreSession(): Promise<boolean> {
  try {
    const refreshRes = await axios.post<{ accessToken: string }>(
      `${BASE_URL}/auth/refresh`,
      {},
      { withCredentials: true },
    );
    const accessToken = refreshRes.data.accessToken;
    const meRes = await axios.get<{
      id: string;
      email: string;
      fullName: string | null;
      roles: string[];
      perms: string[];
      totpEnabled: boolean;
      mustChangePassword?: boolean;
      branchId: string | null;
    }>(`${BASE_URL}/me`, {
      withCredentials: true,
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const u = meRes.data;
    useAuthStore.getState().setSession(accessToken, {
      id: u.id,
      email: u.email,
      fullName: u.fullName,
      roles: u.roles,
      perms: u.perms,
      totpEnabled: u.totpEnabled,
      // Re-hydrated on every boot: the flag must be read HERE too, not only in
      // the login mapper, or a page refresh drops the forced-change gate.
      mustChangePassword: u.mustChangePassword ?? false,
      branchId: u.branchId ?? null,
    });
    return true;
  } catch {
    useAuthStore.getState().clear();
    queryClient.clear();
    return false;
  }
}

export type ReceiptHeader = {
  companyName?: string;
  address?: string;
  tin?: string;
  phone?: string;
  footer?: string;
};

export type Branch = {
  id: string;
  code: string;
  name: string;
  type: string;
  completionMode?: 'auto' | 'manual';
  receiptHeader?: ReceiptHeader;
  /** Which optional blocks the tape prints. Sparse — merge via `resolveReceiptFields`. */
  receiptFields?: Partial<ReceiptFieldFlags>;
  // Per-branch order-number series prefix (e.g. "MB", "SO-"). The numeric
  // series is appended to this on `orders.code`; the UI strips it to show a
  // clean "Sales Order #0001".
  orderPrefix?: string | null;
  // Per-branch cashier-discount policy. Drives the Review modal's cashier
  // discount section + cap-check. (Replaces the old tenant-feature flag.)
  cashierDiscountEnabled?: boolean;
  cashierDiscountMaxPct?: string;
  cashierDiscountMaxAmount?: string;
};

export async function listBranches(): Promise<Branch[]> {
  const res = await api.get<Branch[]>('/branches');
  return res.data;
}

export type KitCode = {
  id: string;
  code: string;
  pin: string;
  kitProductId: string;
  kitMembershipId: string;
};

/** A product variation sellable at the till (branch-scoped price + stock). */
export type PosVariant = {
  id: string;
  sku: string;
  name: string;
  attributes: Record<string, string>;
  /** Branch-effective cash price (branch variant override or variant base). */
  price: string;
  /** BF4: flat per-currency prices (null = not sold in that currency). */
  priceGc: string | null;
  priceCoin: string | null;
  pv: string;
  /** This variant's stock at the selected branch. */
  stockQty: number;
  inStock: boolean;
};

export type PosProduct = {
  id: string;
  sku: string;
  name: string;
  description: string | null;
  basePrice: string;
  effectivePrice: string;
  stockistDiscountPct: string;
  /** BF4: flat per-currency prices (null = not sold in that currency). */
  priceGc: string | null;
  priceCoin: string | null;
  pv: string;
  images: string[];
  featured: boolean;
  /** The kit's (or plain product's) own branch stock. */
  qty: number;
  /**
   * Effective units sellable now: own stock, or — for a kit short on its own
   * stock — how many can be assembled from its inclusive items. For a variation
   * product this is the SUM of in-branch variant stock. The till gates
   * add/disable/qty-cap on THIS, not `qty`.
   */
  sellableQty: number;
  isKit: boolean;
  /** Virtual product / service: not inventory-tracked, always sellable. */
  isVirtual: boolean;
  /** True when `sellableQty` is only achievable via component assembly. */
  assembledFromComponents: boolean;
  /** Active variations; [] for a single-SKU product. A variation product is
   *  sold per-variant (the picker gates add-to-cart on the chosen variant). */
  variants: PosVariant[];
};

/** One row of the append-only stock-movement ledger (Inventory → View log).
 *  Shape mirrors the admin SPA's `InventoryMovement` (cross-app imports are
 *  banned; the type duplicate is the sanctioned cost). */
export type InventoryMovement = {
  id: string;
  productId: string;
  productSku: string;
  productName: string;
  variantId: string | null;
  variantName: string | null;
  variantSku: string | null;
  /** Signed quantity change. */
  delta: number;
  reason: string;
  balanceBefore: number;
  balanceAfter: number;
  refType: string | null;
  refId: string | null;
  actorUserId: string | null;
  actorName: string | null;
  actorEmail: string | null;
  note: string | null;
  createdAt: string;
};

export async function listInventoryMovements(params: {
  branchId: string;
  productId?: string;
  variantId?: string;
  reason?: string;
  from?: string;
  to?: string;
  limit?: number;
}): Promise<InventoryMovement[]> {
  const res = await api.get<InventoryMovement[]>('/pos/inventory/movements', { params });
  return res.data;
}

export async function listPosProducts(branchId: string): Promise<PosProduct[]> {
  const res = await api.get<PosProduct[]>('/pos/products', {
    params: { branchId },
  });
  return res.data;
}

/**
 * Per-slot discount overlay for the branch catalog. Keyed by product / variant
 * id, present only for entries the selected slot actually discounts below SRP
 * (membership / rank / stockist). The grid + cart apply this live over the base
 * catalog so switching slots re-prices instantly. Cash-only.
 */
export type PriceMapEntry = { unitPrice: string; appliedDiscountPct: string };
export type PosPriceMap = {
  products: Record<string, PriceMapEntry>;
  variants: Record<string, PriceMapEntry>;
};

/**
 * `buyerUserId` is required; `buyerSlotId` is optional. With no slot the server
 * still answers — a buyer who has never activated a slot may be priced at the
 * tenant's non-activated tier, and only the server can tell that apart from a
 * cashier deselecting an activated member's slot (which stays at SRP).
 */
export async function getPosPriceMap(
  branchId: string,
  buyerUserId: string,
  buyerSlotId: string | null,
  // "Sell to Stockist" mode: the stockist tier lives on the DESTINATION
  // branch's price rows, so the server has to know this isn't a retail sale or
  // the overlay comes back priced at the buyer's membership tier only.
  stockistTransfer = false,
): Promise<PosPriceMap> {
  const res = await api.get<PosPriceMap>('/pos/price-map', {
    params: {
      branchId,
      buyerUserId,
      ...(buyerSlotId ? { buyerSlotId } : {}),
      ...(stockistTransfer ? { stockistTransfer: 'true' } : {}),
    },
  });
  return res.data;
}

/**
 * QoL Slice 5a — smart search returns BOTH user-level matches (legacy shape)
 * and slot-level matches (carry slotId + slotCode so the cashier can pick
 * a slot directly without a follow-up modal).
 */
export type MemberSearchResult = {
  matchType: 'user' | 'slot';
  id: string;
  email: string;
  phone: string | null;
  fullName: string | null;
  referralCode: string | null;
  slotId: string | null;
  slotCode: string | null;
};

export async function searchMembers(q: string): Promise<MemberSearchResult[]> {
  const res = await api.get<MemberSearchResult[]>('/pos/members/search', {
    params: { q },
  });
  return res.data;
}

export type ActiveSlot = {
  id: string;
  slotCode: string;
  membershipName: string;
  activatedAt: string | null;
};

export async function listMemberActiveSlots(userId: string): Promise<ActiveSlot[]> {
  const res = await api.get<ActiveSlot[]>(`/pos/users/${userId}/slots`);
  return res.data;
}

// The stockist branch a SLOT owns, if any. When set, "Sell to Stockist" mode
// transfers stock into that branch instead of ringing up a normal retail sale.
export type StockistBranch = { id: string; name: string };

export async function getSlotStockistBranch(slotId: string): Promise<StockistBranch | null> {
  const res = await api.get<StockistBranch | null>(`/pos/slots/${slotId}/stockist`);
  return res.data;
}

// Search stockists to sell to (for the Sell to Stockist tab). Results carry the
// owner SLOT (matchType 'slot'), so picking one binds that slot for the transfer.
export async function searchStockists(q: string): Promise<MemberSearchResult[]> {
  const res = await api.get<MemberSearchResult[]>('/pos/stockists/search', { params: { q } });
  return res.data;
}

export type PreviewLine = {
  productId: string;
  productName: string;
  productSku: string;
  qty: number;
  unitPrice: string;
  unitPriceBase: string;
  appliedDiscountPct: string;
  unitPv: string;
};

export type PreviewResult = {
  lines: PreviewLine[];
  subtotal: string;
  discountTotal: string;
  total: string;
  totalPv: string;
  currency: 'cash' | 'gc' | 'coin';
  // Slice 5b: order-level cashier discount, when applied.
  managerDiscountPct: string;
  managerDiscountAmount: string;
};

export async function previewCheckout(payload: {
  branchId: string;
  buyerUserId: string;
  buyerSlotId?: string | null;
  items: Array<{ productId: string; variantId?: string; qty: number }>;
  paymentCurrency?: 'cash' | 'gc' | 'coin';
  // QoL Slice 5b: preview the manager discount alongside the rest so the
  // Review modal can update the post-override total as the cashier types.
  managerDiscount?: { pct?: number; amount?: number };
  // Must match what /pos/checkout is sent, or Review quotes membership pricing
  // and the Charge rings up the stockist price.
  stockistTransfer?: boolean;
}): Promise<PreviewResult> {
  const res = await api.post<PreviewResult>('/pos/checkout/preview', payload);
  return res.data;
}

/** F1 CMS: an admin-managed POS payment method the cashier renders as a tender button. */
export type PosPaymentMethodOption = {
  code: string;
  label: string;
  tenderKind: 'cash' | 'exact' | 'wallet';
  currency: 'cash' | 'gc' | 'coin';
};

export async function getPosPaymentMethods(): Promise<PosPaymentMethodOption[]> {
  const { data } = await api.get<{ methods: PosPaymentMethodOption[] }>('/pos/payment-methods');
  return data.methods;
}

/** One admin-configured custom fee type, as offered at the till. */
export type PosCustomFeeTypeOption = { code: string; label: string };

/** Enabled fee types only — an empty list means the till hides the fee box. */
export async function getPosCustomFeeTypes(): Promise<PosCustomFeeTypeOption[]> {
  const { data } = await api.get<{ types: PosCustomFeeTypeOption[] }>('/pos/custom-fee-types');
  return data.types;
}

export type PosCheckoutPayload = {
  branchId: string;
  buyerUserId: string;
  buyerSlotId?: string | null;
  items: Array<{ productId: string; variantId?: string; qty: number }>;
  payment: {
    method: 'cash' | 'card' | 'wallet';
    amountTendered: string;
    // BF4: pay in cash (default) or debit the buyer's gc/coin wallet balance.
    currency?: 'cash' | 'gc' | 'coin';
    // F1 CMS: the `pos_payment_methods` registry code chosen at the tender step.
    paymentMethodCode?: string;
    idempotencyKey?: string;
  };
  notes?: string;
  // QoL Slice 5b — optional order-level cashier discount. When pct/amount
  // exceed the tenant cap, `approvalChip` (from /pos/discount-override/verify)
  // is required.
  managerDiscount?: {
    pct?: number;
    amount?: number;
    reason?: string;
    approvalChip?: string;
  };
  // Ad-hoc charge typed at the till. Added on top of the discounted goods,
  // never discounted, never PV-bearing. Cash-denominated only.
  /** DEPRECATED single ad-hoc fee. Kept so an in-flight bundle keeps working. */
  customFee?: { label: string; amount: string };
  /** Registry-backed fees: the server resolves each label from `typeCode`. */
  customFees?: Array<{ typeCode: string; amount: string }>;
  // "Sell to Stockist" mode: the sale is a stock transfer into the buyer slot's
  // stockist branch (server validates the slot owns one). Default false.
  stockistTransfer?: boolean;
};

/**
 * Cashier discount config the Review modal reads to decide whether to show
 * the discount section and how to apply the cap. Per-branch (not tenant),
 * sourced from the active Branch row in `listBranches()`.
 */
export type CashierDiscountConfig = {
  enabled: boolean;
  max_pct_without_approval: number;
  max_amount_without_approval: number;
};

export function cashierDiscountFromBranch(b: Branch | null | undefined): CashierDiscountConfig {
  return {
    enabled: !!b?.cashierDiscountEnabled,
    max_pct_without_approval: Number(b?.cashierDiscountMaxPct ?? 0),
    max_amount_without_approval: Number(b?.cashierDiscountMaxAmount ?? 0),
  };
}

/**
 * QoL Slice 5b — verify a manager PIN. Returns a short-lived signed approval
 * chip the cashier passes alongside an above-cap manager discount at
 * /pos/checkout.
 */
export async function verifyManagerPin(args: {
  managerEmail: string;
  pin: string;
}): Promise<{ approvalChip: string; managerId: string; expiresInSeconds: number }> {
  const res = await api.post<{ approvalChip: string; managerId: string; expiresInSeconds: number }>(
    '/pos/discount-override/verify',
    args,
  );
  return res.data;
}

export type ReleaseHistoryRow = {
  orderId: string;
  orderCode: string;
  orderTotal: string;
  releasedAt: string;
  cashierId: string | null;
  cashierEmail: string | null;
  buyerUserId: string;
  branchId: string | null;
  branchCode: string | null;
};

export async function listReleases(
  params: {
    cashierId?: string;
    memberUserId?: string;
    from?: string;
    to?: string;
    limit?: number;
  } = {},
): Promise<ReleaseHistoryRow[]> {
  const res = await api.get<ReleaseHistoryRow[]>('/pos/releases', { params });
  return res.data;
}

/**
 * One sold line, as printed on the receipt tape. Returned identically by
 * `POST /pos/checkout` and `GET /pos/orders/:id` so the checkout tape and the
 * release/reprint tape can never drift.
 */
export type PosReceiptItem = {
  name: string;
  variantName: string | null;
  qty: number;
  unitPrice: string;
  lineTotal: string;
};

export type PosCheckoutResult = {
  order: {
    id: string;
    code: string;
    // BF3: 'completed' (auto mode) or 'to_release' (manual — claim at Releasing).
    status: string;
    subtotal: string;
    discountTotal: string;
    total: string;
    totalPv: string;
    customFeeLabel: string | null;
    customFeeAmount: string;
    /** Per-fee breakdown; `[]` on orders placed before it existed. */
    customFees?: Array<{ code: string | null; label: string; amount: string }> | null;
    branchId: string;
    buyerUserId: string;
    buyerSlotId: string | null;
    notes: string | null;
    createdAt: string;
  };
  payment: {
    id: string;
    /** Coarse tender bucket. `methodLabel` is what the tape should print. */
    method: string;
    methodLabel: string | null;
    amount: string;
    amountTendered: string;
    changeDue: string;
    status: string;
  };
  // BF3: minted at checkout in auto mode; empty in manual mode (minted at release).
  kitCodes: KitCode[];
  /** Sold lines for the tape. Server-provided so the receipt needs no extra fetch. */
  items: PosReceiptItem[];
  /** Who the sale was rung up for / by. Server-resolved — see PosCheckoutResult in the API. */
  buyerName: string | null;
  cashierName: string | null;
  replayed: boolean;
};

export async function posCheckout(payload: PosCheckoutPayload): Promise<PosCheckoutResult> {
  const res = await api.post<PosCheckoutResult>('/pos/checkout', payload);
  return res.data;
}

// ── BF3: releasing area ──────────────────────────────────────────────

export type ReleasingOrder = {
  id: string;
  code: string;
  total: string;
  createdAt: string;
  buyerUserId: string;
  buyerEmail: string;
  buyerName: string | null;
  paymentMethod: string | null;
  /** The tender the cashier picked ("BPI Bank"). Show THIS, not `paymentMethod`. */
  paymentMethodLabel: string | null;
  paymentAmount: string | null;
};

export async function listReleasing(branchId: string): Promise<ReleasingOrder[]> {
  const res = await api.get<ReleasingOrder[]>('/pos/releasing', { params: { branchId } });
  return res.data;
}

export type PosReleaseResult = {
  orderId: string;
  kitCodes: KitCode[];
  buyerSlotId: string | null;
  replayed: boolean;
};

export async function releaseOrder(orderId: string): Promise<PosReleaseResult> {
  const res = await api.post<PosReleaseResult>(`/pos/orders/${orderId}/release`);
  return res.data;
}

export type PosOrderDetail = {
  id: string;
  code: string;
  status: string;
  subtotal: string;
  discountTotal: string;
  total: string;
  customFeeLabel: string | null;
  customFeeAmount: string;
  /** Per-fee breakdown; `[]` on orders placed before it existed. */
  customFees?: Array<{ code: string | null; label: string; amount: string }> | null;
  branchId: string | null;
  createdAt: string;
  releasedAt: string | null;
  notes: string | null;
  /** Amendment stamp for `notes` — null when the remark was never edited. */
  notesUpdatedAt: string | null;
  notesUpdatedByName: string | null;
  buyerName: string | null;
  buyerEmail: string | null;
  /** Till operator on the ORIGINAL payment (payments are oldest-first). */
  cashierName: string | null;
  payments: Array<{
    id: string;
    /** Coarse tender bucket. `methodLabel` is what a human should read. */
    method: string;
    methodLabel: string | null;
    amount: string;
    amountTendered: string;
    changeDue: string;
    status: string;
  }>;
  items: PosReceiptItem[];
  branch: {
    name: string;
    code: string;
    completionMode: string;
    receiptHeader: ReceiptHeader;
    receiptFields: Partial<ReceiptFieldFlags>;
  } | null;
  kitCodes: Array<KitCode & { redeemedAt: string | null }>;
  /** True iff no kit code from this order has been redeemed (server-computed). */
  allRefundable?: boolean;
  /** True iff a completed order may be voided (completed + nothing redeemed). */
  voidable?: boolean;
  /** Reasons captured at a void / refund. Never surfaced anywhere before. */
  reasonHistory: Array<{
    id: string;
    status: string;
    note: string | null;
    changedAt: string;
    changedByName: string | null;
  }>;
};

/** Amend a POS sale's remarks. Requires `pos.memo.edit`. */
export async function updateOrderMemo(orderId: string, notes: string | null) {
  return (await api.patch(`/pos/orders/${orderId}/memo`, { notes })).data;
}

/** Amend the reason captured at a void / refund. Requires `pos.memo.edit`. */
export async function updateOrderReason(orderId: string, historyId: string, note: string) {
  return (await api.patch(`/pos/orders/${orderId}/history/${historyId}/note`, { note })).data;
}

/** Amend an end-of-shift note. Requires `pos.memo.edit`. */
export async function updateShiftCloseNotes(id: string, notes: string | null) {
  return (await api.patch(`/pos/shift-closes/${id}/notes`, { notes })).data;
}

export async function getPosOrder(orderId: string): Promise<PosOrderDetail> {
  const res = await api.get<PosOrderDetail>(`/pos/orders/${orderId}`);
  return res.data;
}

export async function voidOrder(
  orderId: string,
  reason: string,
): Promise<{ paymentVoided: boolean }> {
  const res = await api.post<{ paymentVoided: boolean }>(`/pos/orders/${orderId}/void`, { reason });
  return res.data;
}

// ── FS Activation via Cashier ────────────────────────────────────────

/** An FS-eligible membership tier (admin-flagged) the cashier may hand out. */
export type FsMembership = { id: string; code: string; name: string };

export async function listFsMemberships(): Promise<FsMembership[]> {
  const res = await api.get<FsMembership[]>('/pos/fs-memberships');
  return res.data;
}

export type ActivateFsResult = {
  slotId: string;
  slotCode: string;
  sponsorSlotId: string;
  binaryParentSlotId: string | null;
  binaryPosition: 'L' | 'R' | null;
  membershipId: string;
  membershipCode: string;
};

/** Directly create + activate a FREE (FS) slot for a member — no order/payment. */
export async function activateFsSlot(payload: {
  memberId: string;
  membershipId: string;
  sponsorCode?: string;
  anchorSlotId?: string;
  side?: 'L' | 'R';
}): Promise<ActivateFsResult> {
  const res = await api.post<ActivateFsResult>('/pos/slots/activate-fs', payload);
  return res.data;
}

// ── Cashier area: transactions, daily report, end-of-shift ───────────

export type PosTransaction = {
  orderId: string;
  code: string;
  createdAt: string;
  status: string;
  total: string;
  paymentCurrency: string;
  /** Coarse tender bucket — for logic (e.g. the cash-sales tile), not display. */
  paymentMethod: string | null;
  /** The tender the cashier picked ("BPI Bank"). Show THIS in a Payment column. */
  paymentMethodLabel: string | null;
  paymentAmount: string | null;
  changeDue: string | null;
  cashierId: string | null;
  cashierEmail: string | null;
  itemCount: number;
  /** Whether the sale carries a remark (the text itself is on the detail). */
  hasNotes: boolean;
};

export async function listTransactions(params: {
  branchId: string;
  status?: string;
  cashierId?: string;
  from?: string;
  to?: string;
  q?: string;
  /** F1 CMS registry code — filter by the tender the cashier actually picked. */
  paymentMethod?: string;
  limit?: number;
}): Promise<PosTransaction[]> {
  const res = await api.get<PosTransaction[]>('/pos/transactions', { params });
  return res.data;
}

export type PosTransactionsSummary = {
  totals: Array<{
    currency: string;
    count: number;
    total: string;
    completedCount: number;
    completedTotal: string;
  }>;
  byMethod: Array<{ code: string | null; label: string; count: number; total: string }>;
};

/**
 * Totals over EVERY row matching the same filters as `listTransactions` — the
 * list is capped at 500 rows, so summing it client-side would silently
 * understate a busy range. Same query params minus `limit`.
 */
export async function getTransactionsSummary(params: {
  branchId: string;
  status?: string;
  cashierId?: string;
  from?: string;
  to?: string;
  q?: string;
  paymentMethod?: string;
}): Promise<PosTransactionsSummary> {
  const res = await api.get<PosTransactionsSummary>('/pos/transactions/summary', { params });
  return res.data;
}

// ── Orders tab (branch-scoped port of the admin Orders view) ─────────────────
export type BranchOrder = {
  orderId: string;
  code: string;
  type: string;
  status: string;
  total: string;
  totalPv: string;
  paymentCurrency: string;
  buyerName: string | null;
  buyerEmail: string | null;
  slotCode: string | null;
  trackingNumber: string | null;
  itemCount: number;
  createdAt: string;
};

export type BranchOrderList = {
  rows: BranchOrder[];
  total: number;
  page: number;
  pageSize: number;
};

export async function listBranchOrders(params: {
  branchId: string;
  type?: string;
  status?: string;
  q?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  pageSize?: number;
}): Promise<BranchOrderList> {
  const res = await api.get<BranchOrderList>('/pos/orders', { params });
  return res.data;
}

export type BranchOrderDetail = {
  id: string;
  code: string;
  type: string;
  status: string;
  total: string;
  totalPv: string;
  shippingFee: string;
  paymentCurrency: string;
  courierId: string | null;
  trackingNumber: string | null;
  shipToAddress: Record<string, unknown> | null;
  buyerName: string | null;
  buyerEmail: string | null;
  branchId: string | null;
  createdAt: string;
  items: Array<{
    name: string;
    variantName: string | null;
    qty: number;
    unitPrice: string;
    lineTotal: string;
  }>;
  branch: { name: string; code: string; completionMode: string; receiptHeader: unknown } | null;
  fulfillEdges: Array<{ from: string; to: string; requires?: string }>;
};

export async function getBranchOrder(orderId: string): Promise<BranchOrderDetail> {
  const res = await api.get<BranchOrderDetail>(`/pos/orders/${orderId}/detail`);
  return res.data;
}

export async function fulfillBranchOrder(
  orderId: string,
  payload: { status: string; note?: string; trackingNumber?: string },
): Promise<{ status: string }> {
  const res = await api.post<{ status: string }>(`/pos/orders/${orderId}/fulfill`, payload);
  return res.data;
}

export type DailySalesReport = {
  branchId: string;
  from: string;
  to: string;
  ordersCount: number;
  grossSales: string;
  pvTotal: string;
  refundsCount: number;
  refundsTotal: string;
  paymentBreakdown: Array<{ method: string; count: number; total: string }>;
  topItems: Array<{
    productId: string;
    sku: string;
    name: string;
    variantName: string | null;
    qty: number;
    total: string;
  }>;
};

export async function getDailyReport(params: {
  branchId: string;
  from: string;
  to: string;
}): Promise<DailySalesReport> {
  const res = await api.get<DailySalesReport>('/pos/daily-report', { params });
  return res.data;
}

export type ShiftCloseResult = {
  record: {
    id: string;
    cashierUserId: string;
    branchId: string | null;
    startAt: string;
    endAt: string;
    expectedCash: string;
    actualCash: string;
    variance: string;
    notes: string | null;
  };
  expectedCash: string;
  variance: string;
};

export async function shiftClose(payload: {
  branchId: string;
  startAt: string;
  actualCash: number;
  notes?: string;
}): Promise<ShiftCloseResult> {
  const res = await api.post<ShiftCloseResult>('/pos/shift-close', payload);
  return res.data;
}

export type ShiftCloseRow = {
  id: string;
  cashierUserId: string;
  cashierEmail: string | null;
  branchId: string | null;
  startAt: string;
  endAt: string;
  expectedCash: string;
  actualCash: string;
  variance: string;
  notes: string | null;
  /** Amendment stamp — set when the note was edited after the shift closed. */
  notesUpdatedAt: string | null;
  notesUpdatedByName: string | null;
};

export async function listShiftCloses(
  params: {
    branchId?: string;
    cashierId?: string;
    limit?: number;
  } = {},
): Promise<ShiftCloseRow[]> {
  const res = await api.get<ShiftCloseRow[]>('/pos/shift-closes', { params });
  return res.data;
}

/** Public branding/config endpoint (no auth) — only the fields the till needs. */
export async function getPublicTenantConfig(): Promise<{
  /** Tenant's display name for the coin wallet (BF4 `coin_label`), e.g. "UPCoin". */
  coinLabel: string;
}> {
  const res = await api.get<{ coinLabel?: string }>('/tenant/config');
  return { coinLabel: res.data.coinLabel?.trim() || DEFAULT_COIN_LABEL };
}
